import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchNew, fetchMenu } from "../../api/newsService";
import ReactPaginate from "react-paginate";
import { ArrowUpRight, CalendarDays } from "lucide-react";
import CategorySidebar from "../../components/Sidebar/CategorySidebar";
import "./newspaper.scss";
import { slugify } from "../../components/Sdata.js";
import { TitleComponent } from "../../components/TitleDocument";
import BreadCrumb from "../../components/BreadCrumb";
import newsBackground from "../../assets/news_bg.svg";
import dvvhData from "../../data/dichVuVanHanh.json";
import renderNoBreak from "../../components/renderNoBreak";

const SERVICE_SECTION_SLUGS = new Set([
  slugify("Dịch vụ-Hạ tầng"),
  slugify("Dịch vụ hạ tầng"),
]);

const SERVICE_INTRO_COPY = {
  title: "Dịch vụ hạ tầng",
  description:
    "VNPT Net cung cấp hệ sinh thái hạ tầng số toàn diện với các dịch vụ Internet, truyền dẫn, VPN và trung tâm dữ liệu, mang đến kết nối mạnh mẽ, ổn định và an toàn, giúp doanh nghiệp tối ưu vận hành và sẵn sàng bứt phá trong hành trình chuyển đổi số.",
};

function isServiceCategoryBranch(categoryId, categoryMetaMap) {
  if (!categoryId || !categoryMetaMap[categoryId]) {
    return false;
  }

  let current = categoryMetaMap[categoryId];

  while (current) {
    if (SERVICE_SECTION_SLUGS.has(slugify(current.name))) {
      return true;
    }

    current = current.parentId ? categoryMetaMap[current.parentId] : null;
  }

  return false;
}

const NewsPaper = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [postNew, setPostNew] = useState([]);
  const [idMap, setIdMap] = useState({});
  const [categoryTitles, setCategoryTitles] = useState({});
  const [categoryMetaMap, setCategoryMetaMap] = useState({});
  const [currentCategoryId, setCurrentCategoryId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);

  const isServiceLayout = isServiceCategoryBranch(currentCategoryId, categoryMetaMap);
  const postsPerPage = isServiceLayout ? 9 : 5;

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const categoryRes = await fetchMenu();
      if (categoryRes?.data?.data) {
        const meta = {};
        setIdMap(() => {
          const map = {};
          const titles = {};

          categoryRes.data.data.forEach((item) => {
            const slug = slugify(item.name);
            map[slug] = item.id;
            titles[item.id] = item.name;
            meta[item.id] = item;
            localStorage.setItem(slug, item.name);
          });

          setCategoryTitles(titles);
          return map;
        });
        setCategoryMetaMap(meta);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCategoryTitleWithAccent = (slug) => {
    const titleWithAccent = localStorage.getItem(slug);
    return titleWithAccent ? titleWithAccent : slugify(slug);
  };

  const getData = useCallback(async () => {
    if (!currentCategoryId) return;

    setIsLoading(true);
    try {
      const res = await fetchNew("", currentCategoryId, page + 1, postsPerPage);
      if (res?.data?.data?.lstNews) {
        setPostNew(res.data.data.lstNews);
        setTotalPages(Math.ceil(res.data.data.totalRecord / postsPerPage));
      }
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentCategoryId, page, postsPerPage]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (Object.keys(idMap).length > 0 && categoryId) {
      setPage(0);
      const id = idMap[categoryId];
      if (id) {
        setCurrentCategoryId(id);
      }
    }
  }, [categoryId, idMap]);

  useEffect(() => {
    if (currentCategoryId) {
      getData();
    }
  }, [currentCategoryId, page, getData]);

  const changePage = ({ selected }) => {
    setPage(selected);
  };

  const title = categoryTitles[currentCategoryId] || "Tin tức";
  const sidebarFeaturedNewsPage = categoryId === "tin-noi-bat" ? page + 2 : 1;

  const renderDefaultList = () => (
    <>
      <div className="news_paper__list">
        {postNew?.length > 0 ? (
          postNew.map((employee) => (
            <article key={employee.id} className="news_paper__card">
              <Link to={`/${categoryId}/${employee.id}`} className="news_paper__card-link">
                <div className="news_paper__media">
                  <img src={employee.image} alt={employee.title} />
                </div>

                <div className="news_paper__content">
                  <div className="news_paper__meta">
                    <span className="news_paper__badge">{title}</span>
                    {employee.publicDate ? (
                      <span className="news_paper__date">
                        <CalendarDays size={14} />
                        {employee.publicDate}
                      </span>
                    ) : null}
                  </div>

                  <h2>{employee.title}</h2>
                  <p>{employee.description}</p>

                  <span className="news_paper__cta">
                    Xem chi tiết
                    <ArrowUpRight size={16} />
                  </span>
                </div>
              </Link>
            </article>
          ))
        ) : (
          <div className="news_paper__empty">
            <p>Chưa có nội dung để hiển thị.</p>
          </div>
        )}
      </div>

      {totalPages > 1 ? (
        <ReactPaginate
          previousLabel={"Trước"}
          nextLabel={"Sau"}
          pageCount={totalPages}
          onPageChange={changePage}
          containerClassName={"news_paper__pagination"}
          previousLinkClassName={"news_paper__page-link"}
          nextLinkClassName={"news_paper__page-link"}
          disabledClassName={"news_paper__page-disabled"}
          activeClassName={"news_paper__page-active"}
          pageClassName={"news_paper__page-item"}
          pageLinkClassName={"news_paper__page-link"}
          previousClassName={"news_paper__page-item"}
          nextClassName={"news_paper__page-item"}
          breakClassName={"news_paper__page-item"}
          breakLinkClassName={"news_paper__page-link"}
          pageRangeDisplayed={2}
          marginPagesDisplayed={1}
          forcePage={page}
        />
      ) : null}
    </>
  );

  const renderServiceList = () => (
    <div className="news_paper__service-stage">
      <div
        className="news_paper__service-bg"
        style={{ backgroundImage: `url(${newsBackground})` }}
        aria-hidden="true"
      />

      <section className="news_paper__service-intro">
        <div className="news_paper__service-intro-inner">
          <h1>{SERVICE_INTRO_COPY.title}</h1>
          <p>{SERVICE_INTRO_COPY.description}</p>
        </div>
      </section>

      <div className="news_paper__list news_paper__list--service">
        {page === 0 ? (
          <Link
            to="/dich-vu-van-hanh"
            className="news_paper__promo"
            aria-label={dvvhData.banner.title}
          >
            {dvvhData.banner.image ? (
              <div className="news_paper__promo-bg" aria-hidden="true">
                <img
                  src={dvvhData.banner.image}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
            ) : null}
            <div className="news_paper__promo-overlay" aria-hidden="true" />

            <div className="news_paper__promo-inner">
            <span className="news_paper__promo-eyebrow">{dvvhData.banner.eyebrow}</span>
            <h2 className="news_paper__promo-title">{renderNoBreak(dvvhData.banner.title)}</h2>
            <p className="news_paper__promo-intro">{renderNoBreak(dvvhData.banner.intro)}</p>
            <div className="news_paper__promo-actions">
              <span
                role="button"
                tabIndex={0}
                className="news_paper__promo-btn news_paper__promo-btn--primary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate("/dich-vu-van-hanh");
                }}
              >
                {dvvhData.banner.primaryButton}
              </span>
              <span
                role="button"
                tabIndex={0}
                className="news_paper__promo-btn news_paper__promo-btn--ghost"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate("/dich-vu-van-hanh?scroll=form");
                }}
              >
                {dvvhData.banner.secondaryButton}
              </span>
            </div>
            </div>
          </Link>
        ) : null}
        {postNew?.length > 0 ? (
          postNew.map((employee) => (
            <article key={employee.id} className="news_paper__card news_paper__card--service">
              <Link to={`/${categoryId}/${employee.id}`} className="news_paper__card-link news_paper__card-link--service">
                <div className="news_paper__media">
                  <img src={employee.image} alt={employee.title} />
                </div>

                <div className="news_paper__content">
                  <h2>{employee.title}</h2>

                  <span className="news_paper__cta">
                    Xem chi tiết
                    <ArrowUpRight size={16} />
                  </span>
                </div>
              </Link>
            </article>
          ))
        ) : (
          <div className="news_paper__empty">
            <p>Chưa có nội dung để hiển thị.</p>
          </div>
        )}
      </div>

      {totalPages > 1 ? (
        <ReactPaginate
          previousLabel={"Trước"}
          nextLabel={"Sau"}
          pageCount={totalPages}
          onPageChange={changePage}
          containerClassName={"news_paper__pagination"}
          previousLinkClassName={"news_paper__page-link"}
          nextLinkClassName={"news_paper__page-link"}
          disabledClassName={"news_paper__page-disabled"}
          activeClassName={"news_paper__page-active"}
          pageClassName={"news_paper__page-item"}
          pageLinkClassName={"news_paper__page-link"}
          previousClassName={"news_paper__page-item"}
          nextClassName={"news_paper__page-item"}
          breakClassName={"news_paper__page-item"}
          breakLinkClassName={"news_paper__page-link"}
          pageRangeDisplayed={2}
          marginPagesDisplayed={1}
          forcePage={page}
        />
      ) : null}
    </div>
  );

  return (
    <section className={`news_paper${isServiceLayout ? " news_paper--service" : ""}`}>
      <TitleComponent title={title} />

      <div className="news_paper__shell">
        <div className="news_paper__crumb container">
          <BreadCrumb caseCondition="case1" categoryTitle={getCategoryTitleWithAccent(categoryId)} />
        </div>

        <div className="news_on container">
          <div className="news_paper__layout">
            <div className="news_paper__main">
              {isLoading ? (
                <div className="news_paper__loading">
                  <p>Loading...</p>
                </div>
              ) : isServiceLayout ? (
                renderServiceList()
              ) : (
                renderDefaultList()
              )}
            </div>

            {!isServiceLayout ? (
              <aside className="news_paper__sidebar">
                <CategorySidebar featuredNewsPage={sidebarFeaturedNewsPage} />
              </aside>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsPaper;
